import { memo, useCallback, useEffect, useRef, useState } from 'react'
import { useDebouncedCallback } from 'use-debounce'

import { fetchViewModeDrawResults, ViewModeActions, ViewModeState } from '@AppState/view-mode'
import { AnimationControls } from './animation-controls'
import { GameController } from '@Animation/game-control'
import { DrawAnimationResult } from '@Animation/types'
import { pollingRequest, RootState, useAppDispatch } from '@AppState/redux-store'

import './styles.scss'
import { AnimationDrawInfoPanel } from '../draw-info-panel/animation-draw-info-panel'
import { AnimationActions, ANIMATION_PLAY_STATE } from '@AppState/animation'
import { PollingActions } from '@AppState/polling/polling-slice'
import { fetchLatestDraw } from '@AppState/latest-draw'
import { JumpToDrawDrawer } from '@Components/watch-animation/jump-to-draw-drawer'
import Analytics, { AnalyticEvents } from '@Util/analytics'
import { useSelector } from 'react-redux'
import { UnscoredDrawResult } from '@API/game-results/types'
import { WelcomeDrawer } from '@Components/welcome-drawer/welcome-drawer'
import { DataFetchingState } from '@AppState/types'
import { selectAnimationIdentifier } from '@Animation/utils'

const LATEST_DRAW_POLLING_ID = 'fetchLatestDraw'
const FETCH_DRAW_RESULTS_POLLING_ID = 'fetchViewModeDrawResult'

export type ButtonControl = {
  onClick: () => void
  disabled: boolean
}

export type ViewModeControlProps = {
  animationReady: boolean
  currentDrawNumber: number
  viewModeData: ViewModeState
  latestDraw: number
  onDrawingChange: (drawNumber: number) => void
}

export const ViewModeController = memo(
  (props: ViewModeControlProps) => {
    const gameConfig = useSelector((state: RootState) => state.gameConfig.gameRules)
    const pollingIntervalMap = useSelector((state: RootState) => state.polling.intervalMap)
    const { currentDrawNumber, viewModeData, latestDraw, onDrawingChange } = props
    const drawResults = viewModeData.drawResults
    const [isJumpToDrawOpen, setJumpToDrawOpen] = useState<boolean>(false)
    const [currentSelectedNumber, setCurrentSelectedNumber] = useState<number | null>(null)
    const currentDrawResult: React.MutableRefObject<UnscoredDrawResult | undefined> = useRef(
      drawResults[currentDrawNumber],
    )

    const debounceAnimationChange = useDebouncedCallback((config: DrawAnimationResult) => {
      drawAnimationResult.current = config

      dispatch(
        AnimationActions.playNewDraw({
          result: config,
          identifier: selectAnimationIdentifier(),
        }),
      )
    }, 500)

    const debounceFetchResult = useDebouncedCallback((drawNumber: number) => {
      dispatch(
        pollingRequest(fetchViewModeDrawResults(drawNumber), FETCH_DRAW_RESULTS_POLLING_ID, 30000),
      )
    }, 500)

    const showWelcomeDrawer = useSelector(
      (state: RootState) => state.viewMode.shouldShowWelcomeDrawer,
    )

    const dispatch = useAppDispatch()

    const welcomeDrawerChangeHandler = useCallback(
      (open: boolean) => {
        dispatch(ViewModeActions.updateShouldShowWelcomeDrawer(open))
      },
      [dispatch],
    )

    const drawChangeHandler = useCallback(
      (newDrawNumber: number) => {
        GameController.stopDrawAnimation()
        currentDrawResult.current = undefined
        onDrawingChange(newDrawNumber)
      },
      [onDrawingChange],
    )

    const drawAnimationResult: React.MutableRefObject<DrawAnimationResult | undefined> =
      useRef(undefined)

    const animationDisplayNumberIndex = useSelector(
      (state: RootState) => state.animation.displayNumberIndex,
    )

    useEffect(() => {
      if (
        !pollingIntervalMap[FETCH_DRAW_RESULTS_POLLING_ID] &&
        viewModeData.fetchState !== DataFetchingState.PENDING &&
        !drawResults[currentDrawNumber] &&
        currentDrawNumber <= latestDraw
      ) {
        debounceFetchResult(currentDrawNumber)
      }

      if (
        pollingIntervalMap[FETCH_DRAW_RESULTS_POLLING_ID] &&
        (viewModeData.fetchState === DataFetchingState.SUCCEEDED || drawResults[currentDrawNumber])
      ) {
        dispatch(PollingActions.clearPollingInterval(FETCH_DRAW_RESULTS_POLLING_ID))
      }
    }, [
      currentDrawNumber,
      viewModeData,
      drawResults,
      dispatch,
      latestDraw,
      pollingIntervalMap,
      debounceFetchResult,
    ])

    useEffect(() => {
      if (currentDrawResult.current?.drawingNumber === currentDrawNumber) return
      currentDrawResult.current = drawResults[currentDrawNumber]
    }, [currentDrawNumber, drawResults])

    useEffect(() => {
      if (currentDrawResult.current?.winningNumbers) {
        if (
          animationDisplayNumberIndex >= currentDrawResult.current.winningNumbers.length &&
          currentDrawResult.current.extraDrawAddOnResult
        ) {
          setCurrentSelectedNumber(
            currentDrawResult.current.extraDrawAddOnResult.results[
              animationDisplayNumberIndex - currentDrawResult.current.winningNumbers.length
            ].result,
          )
        } else {
          setCurrentSelectedNumber(
            currentDrawResult.current.winningNumbers[animationDisplayNumberIndex],
          )
        }
      }
    }, [animationDisplayNumberIndex])

    useEffect(() => {
      if (currentDrawNumber > latestDraw) {
        GameController.stopDrawAnimation()
        drawAnimationResult.current = undefined
        dispatch(AnimationActions.setPlayState(ANIMATION_PLAY_STATE.READY))
        dispatch(pollingRequest(fetchLatestDraw(), LATEST_DRAW_POLLING_ID))
      }
      if (currentDrawResult.current) {
        dispatch(PollingActions.clearPollingInterval(LATEST_DRAW_POLLING_ID))
      }
    }, [currentDrawNumber, latestDraw, drawResults, dispatch, viewModeData])

    useEffect(() => {
      dispatch(AnimationActions.setPaused(showWelcomeDrawer || isJumpToDrawOpen))

      return () => {
        dispatch(PollingActions.clearPollingInterval(LATEST_DRAW_POLLING_ID))
        dispatch(PollingActions.clearPollingInterval(FETCH_DRAW_RESULTS_POLLING_ID))
      }
    }, [dispatch, showWelcomeDrawer, isJumpToDrawOpen])

    const playDraw = useCallback(
      (drawResultToPlay: UnscoredDrawResult) => {
        if (drawResultToPlay.drawingNumber === currentDrawNumber) {
          const config: DrawAnimationResult = {
            winningNumbers: drawResultToPlay.winningNumbers.map((num) => {
              return {
                cumulativeDrawingWinningsInCents: 0,
                cumulativeTicketWinningsInCents: 0,
                addonName: null,
                numberSelection: num,
              }
            }),
            ticketInfo: {
              baseSelectedNumbers: [],
              miniGameSelectedNumbers: [],
              multiplierAddOn: drawResultToPlay.multiplierAddOnResult?.name,
            },
            miniGameAddOnResult: drawResultToPlay.miniGameAddOnResult,
            multiplierAddOnResult: drawResultToPlay.multiplierAddOnResult,
            extraDrawAddOnResult: drawResultToPlay.extraDrawAddOnResult,
            drawNumber: currentDrawNumber,
          }

          dispatch(PollingActions.clearPollingInterval(FETCH_DRAW_RESULTS_POLLING_ID))
          drawChangeHandler(config.drawNumber)

          debounceAnimationChange(config)
        }
      },
      [currentDrawNumber, drawChangeHandler, dispatch, debounceAnimationChange],
    )

    useEffect(() => {
      return () => {
        drawAnimationResult.current = undefined
        dispatch(AnimationActions.clearDrawResult())
      }
    }, [dispatch])

    useEffect(() => {
      const hasNewDraw = drawResults[currentDrawNumber] !== undefined

      if (hasNewDraw && drawAnimationResult.current?.drawNumber !== currentDrawNumber) {
        playDraw(drawResults[currentDrawNumber])
      }
    }, [playDraw, currentDrawNumber, drawResults])

    const onJumpToDrawerClick = useCallback(() => {
      setJumpToDrawOpen(true)
    }, [])

    const onNextDrawClick = useCallback(() => {
      if (!currentDrawNumber) {
        return
      }
      const newResultIndex = currentDrawNumber + 1
      Analytics.trackDrawing(AnalyticEvents.NEXT_DRAWING, currentDrawNumber)
      drawChangeHandler(newResultIndex)
    }, [currentDrawNumber, drawChangeHandler])

    const onPrevDrawClick = useCallback(() => {
      if (!currentDrawNumber) {
        return
      }
      const newResultIndex = currentDrawNumber - 1
      if (newResultIndex >= 0) {
        Analytics.trackDrawing(AnalyticEvents.PREVIOUS_DRAWING, currentDrawNumber)
        drawChangeHandler(newResultIndex)
      }
    }, [currentDrawNumber, drawChangeHandler])

    return (
      <>
        <AnimationDrawInfoPanel
          drawNumber={currentDrawNumber}
          winsThisDraw={null}
          winsTotal={null}
          currentSelectedNumber={currentSelectedNumber}
        />

        <AnimationControls
          prevButtonControl={{
            onClick: onPrevDrawClick,
            disabled: currentDrawNumber
              ? currentDrawNumber <= (gameConfig?.at(0)?.activeDrawNumberRange.min || 2)
              : true,
          }}
          jumpToDrawButtonControl={{
            onClick: onJumpToDrawerClick,
            disabled: false,
          }}
          nextButtonControl={{
            onClick: onNextDrawClick,
            disabled: currentDrawNumber
              ? currentDrawNumber >=
                Math.min(latestDraw + 1, gameConfig?.at(-1)?.activeDrawNumberRange.max || Infinity)
              : true,
          }}
        />
        {isJumpToDrawOpen ? (
          <JumpToDrawDrawer
            currentDrawNumber={currentDrawNumber}
            latestDraw={latestDraw}
            open={true}
            onClose={() => {
              setJumpToDrawOpen(false)
            }}
            onSubmit={(drawNumber) => {
              setJumpToDrawOpen(false)

              if (drawNumber === currentDrawNumber) return
              drawChangeHandler(drawNumber)
              Analytics.trackDrawing(AnalyticEvents.JUMP_TO_DRAWING, drawNumber)
            }}
          />
        ) : null}
        {showWelcomeDrawer ? (
          <WelcomeDrawer
            currentDrawNumber={currentDrawNumber}
            onOpenChange={welcomeDrawerChangeHandler}
          />
        ) : null}
      </>
    )
  },
  (prevProps: ViewModeControlProps, nextProps: ViewModeControlProps) => {
    return JSON.stringify(prevProps) === JSON.stringify(nextProps)
  },
)
